import { Building, MoreVertical, Trash2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCompanyStore } from "../hooks/useCompanyStore";
import { useTran } from "../../../hooks/useTran";
import { getLabel } from "../../../utils/utils";
import dayjs from "dayjs";
import { MyButton, MyDropdown } from "../../../components/Commons";
import UserDetailIcon from "../../../assets/icons/UserDetailIcon";
import {
  COMPANY_TYPE_OPTIONS,
  INDUSTRIAL_CLASSIFICATION,
} from "../../OnBoard/constants/onboard.constant";
import { useUserStore } from "../../User/hooks/useUserStore";
import { COMPANY } from "../constants/companyText.constants";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../Auth";
import { SYSTEM_ADMIN } from "../../../constants/constants";
import { useCNPStore } from "../../CNP/hooks/useCNPStore";

const CompanyCard = observer(
  ({ company, companyModulePermission, userModulePermission }) => {
    const { t } = useTran();
    const {
      setCompanyForm,
      openDeleteConfirmModal,
      setSelectedCompanyId,
      openCompanyModal,
      openDuplicateCompanyModal,
    } = useCompanyStore();
    const { openUserModal } = useUserStore();
    const { setSelectedAsset } = useCNPStore();
    const {
      _id,
      companyName,
      country,
      address,
      businessNo,
      companyType,
      industrySector,
      createdAt,
    } = company || {};

    const { role } = useAuthStore();

    const navigate = useNavigate();

    const formattedDate = dayjs(createdAt).format("DD/MM/YYYY h:mmA");

    const _handleEdit = (id) => {
      setCompanyForm(id);
      openCompanyModal();
    };

    const _handleView = () => {
      setSelectedCompanyId(_id);
      setSelectedAsset();
      navigate(`/onboarding/0/${_id}`);
    };

    const { create, update, drop } = companyModulePermission;
    const showViewAsCompany = role !== SYSTEM_ADMIN;
    const actionCount = [drop, showViewAsCompany].filter(Boolean).length;
    const actionGridClass =
      {
        1: "grid-cols-1",
        2: "grid-cols-2",
      }[actionCount] || "grid-cols-1";
    const menuItems = [
      update && {
        label: t(COMPANY.EDIT_COMPANY),
        onClick: () => _handleEdit(_id),
        dataCy: "edit-company",
      },
      userModulePermission?.create && {
        label: t("Add User"),
        onClick: () => openUserModal(_id),
        dataCy: "add-user",
      },
      create && {
        label: t(COMPANY.DUPLICATE_COMPANY),
        onClick: () => openDuplicateCompanyModal(company),
        dataCy: "duplicate-company",
      },
    ].filter(Boolean);

    return (
      <div className="rounded-md bg-secondary flex flex-col gap-y-4 p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-x-2">
            <div className="flex items-center justify-center h-12 w-12 rounded">
              <Building size={30} className="text-blue-500" />
            </div>
            <div>
              <p className="font-semibold">{companyName}</p>
              <p className="text-sm">{t(country)}</p>
            </div>
          </div>
          {menuItems.length > 0 && (
            <MyDropdown
              align="right"
              trigger={
                <div
                  data-cy="company-actions-menu"
                  className="p-1 rounded-md hover:bg-background transition-colors"
                >
                  <MoreVertical className="text-primary" size={18} />
                </div>
              }
              items={menuItems}
            />
          )}
        </div>
        <hr />
        <div className="flex-1 min-h-0 grid grid-cols-3 gap-3">
          <p className="text-gray-500">{t("Business No.")}</p>
          <p className="col-span-2">{businessNo || "-"}</p>
          <p className="text-gray-500">{t("Created Date")}</p>
          <p className="col-span-2">{formattedDate || "-"}</p>
          <p className="text-gray-500">{t("Company Type")}</p>
          <p className="col-span-2">
            {getLabel(COMPANY_TYPE_OPTIONS, companyType) || "-"}
          </p>
          <p className="text-gray-500">{t("Industry Sector")}</p>
          <p className="col-span-2">
            {getLabel(INDUSTRIAL_CLASSIFICATION, industrySector) || "-"}
          </p>
          <p className="text-gray-500">{t("Address")}</p>
          <p className="col-span-2">{address || "-"}</p>
        </div>
        <hr />
        {actionCount > 0 && (
          <div className={`grid ${actionGridClass} gap-3`}>
            {drop && (
              <MyButton
                variant="outline"
                className="bg-background text-danger gap-x-1"
                onClick={() => openDeleteConfirmModal(_id, companyName)}
              >
                <Trash2 size={16} className="text-error" />{" "}
                {t(COMPANY.DELETE_COMPANY)}
              </MyButton>
            )}
            {showViewAsCompany && (
              <MyButton
                variant="outline"
                className="bg-background text-primary gap-x-1"
                onClick={_handleView}
              >
                <UserDetailIcon size={16} color="#109F40" />{" "}
                {t(COMPANY.VIEW_AS_COMPANY)}
              </MyButton>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default CompanyCard;
